import React, { ReactNode, useEffect, useRef, useState } from "react"
import { lazy, Suspense } from "react"
import { createPortal } from "react-dom"
import BookNavModal from "./BookNavModal"
import Provider from "./Provider"

const SubNav = lazy(() => import("./SubNav"))
const BookHeader = lazy(() => import("./BookHeader"))
const BookFooter = lazy(() => import("./BookFooter"))
const LanguageToggle = lazy(() => import("./LanguageToggle"))
const Tribute = lazy(() => import("./Tribute"))
const HelpModal = lazy(() => import("./HelpModal"))
const MenuNavigation = lazy(() => import("./MenuNavigation"))

const Root = ({
  inBook,
  previous,
  next,
}: {
  inBook: boolean
  previous?: string
  next?: string
}) => {
  const tribute = useRef(document.getElementById("tribute"))
  const languageToggle = useRef(document.getElementById("language-toggle"))
  const menuNavigation = useRef(document.getElementById("menu-navigation"))
  const readBibleButtons = useRef(document.querySelectorAll(".app\\:openBible"))
  const bookHeader = useRef(document.getElementById("book-header"))
  const subNav = useRef(document.getElementById("sub-nav"))
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isBookNavOpen, setIsBookNavOpen] = useState(false)

  // @TODO: is this really helping the flash of content?
  const emptyAndRenderPortal = (children: ReactNode, container: Element | null) => {
    if (container) {
      if (container.firstElementChild) {
        const span = document.createElement("span")
        container.firstElementChild.replaceWith(span)
      }
      const portal = createPortal(children, container)
      return portal
    }
    return null
  }

  useEffect(() => {
    const sources = readBibleButtons.current
    const events = ["click", "touchstart"]
    const handleEvent = (event: Event) => {
      event.preventDefault()
      event.stopImmediatePropagation()
      setIsBookNavOpen(true)
    }

    if (sources.length) {
      sources.forEach(source => {
        events.forEach(evt => {
          source.addEventListener(evt, handleEvent, true)
        })
      })
    }

    return () => {
      sources.forEach(source => {
        events.forEach(evt => {
          source.removeEventListener(evt, handleEvent, true)
        })
      })
    }
  }, [])

  useEffect(() => {
    if (inBook) {
      ;(async function () {
        await import("../footnotes")
      })()
    }
  }, [inBook])

  return (
    <>
      <React.StrictMode>
        <Provider
          isBookNavOpen={isBookNavOpen}
          setIsBookNavOpen={setIsBookNavOpen}
          isHelpOpen={isHelpOpen}
          setIsHelpOpen={setIsHelpOpen}
        >
          <Suspense fallback={null}>
            {inBook && (
              <>
                {bookHeader.current && createPortal(<BookHeader />, bookHeader.current)}
                <BookFooter previous={previous} next={next} />
              </>
            )}
            {tribute.current && createPortal(<Tribute />, tribute.current)}
            {languageToggle.current && createPortal(<LanguageToggle />, languageToggle.current)}
            {isHelpOpen && <HelpModal setIsOpen={setIsHelpOpen} isOpen={isHelpOpen} />}
            <BookNavModal />
            {emptyAndRenderPortal(<SubNav />, subNav.current)}
            {emptyAndRenderPortal(<MenuNavigation align="right" />, menuNavigation.current)}
          </Suspense>
        </Provider>
      </React.StrictMode>
    </>
  )
}

export default Root
